export const tabs = {
    jobArchitecture: 'job-architecture',
    organization: 'organization',
    personalPreferences: 'personal-preferences',
    positionArchitecture: 'position-architecture',
    subscriptionBilling: 'subscription-billing',
} as const;

export type TabId = (typeof tabs)[keyof typeof tabs];

export const sidebar = {
    jobArchitecture: {
        careerFramework: 'career-framework',
        companyProfile: 'ja-company-profile',
        jobProfile: 'job-profile',
    },
    organization: {
        companyGoals: 'company-goals',
        companyProfile: 'company-profile',
        knowledgeBase: 'knowledge-base',
        languages: 'languages',
        skillChangeEvents: 'skill-change-events',
        skills: 'skills',
        terminology: 'terminology',
        usersAndRoles: 'users-and-roles',
    },
    personalPreferences: {
        account: 'account',
        apiKeys: 'api-keys',
        languageAndVisibility: 'language-and-visibility',
        notifications: 'notifications',
    },
    positionArchitecture: {
        defaultArchitecture: 'default-position-architecture',
    },
    subscriptionBilling: {
        languages: 'sb-languages',
    },
} as const;

export type OrganizationSidebarId = (typeof sidebar.organization)[keyof typeof sidebar.organization];
export type JobArchitectureSidebarId = (typeof sidebar.jobArchitecture)[keyof typeof sidebar.jobArchitecture];
export type PersonalPreferencesSidebarId =
    (typeof sidebar.personalPreferences)[keyof typeof sidebar.personalPreferences];
export type PositionArchitectureSidebarId =
    (typeof sidebar.positionArchitecture)[keyof typeof sidebar.positionArchitecture];
export type SubscriptionBillingSidebarId =
    (typeof sidebar.subscriptionBilling)[keyof typeof sidebar.subscriptionBilling];
export type SidebarId =
    | JobArchitectureSidebarId
    | OrganizationSidebarId
    | PersonalPreferencesSidebarId
    | PositionArchitectureSidebarId
    | SubscriptionBillingSidebarId;

export const careerFrameworkView = {
    details: 'details',
    list: 'list',
    selectPredefined: 'select-predefined',
} as const;

export type CareerFrameworkViewId = (typeof careerFrameworkView)[keyof typeof careerFrameworkView];

export const frameworkMode = {
    createFromTemplate: 'createFromTemplate',
    edit: 'edit',
} as const;

export type FrameworkModeId = (typeof frameworkMode)[keyof typeof frameworkMode];

export const jobProfileView = {
    configure: 'configure',
    createCustom: 'create-custom',
    customEditor: 'custom-editor',
    defaultEditor: 'default-editor',
    landing: 'landing',
} as const;

export type JobProfileViewId = (typeof jobProfileView)[keyof typeof jobProfileView];

export const defaults = {
    jobArchitectureSidebar: sidebar.jobArchitecture.companyProfile,
    organizationSidebar: sidebar.organization.companyProfile,
    personalPreferencesSidebar: sidebar.personalPreferences.account,
    positionArchitectureSidebar: sidebar.positionArchitecture.defaultArchitecture,
    subscriptionBillingSidebar: sidebar.subscriptionBilling.languages,
} as const;

export const isJobArchitectureSidebarId = (value: SidebarId | undefined): value is JobArchitectureSidebarId =>
    Object.values(sidebar.jobArchitecture).some((v) => v === value);

export const isOrganizationSidebarId = (value: SidebarId | undefined): value is OrganizationSidebarId =>
    Object.values(sidebar.organization).some((v) => v === value);

export const isPersonalPreferencesSidebarId = (value: SidebarId | undefined): value is PersonalPreferencesSidebarId =>
    Object.values(sidebar.personalPreferences).some((v) => v === value);

export const isPositionArchitectureSidebarId = (value: SidebarId | undefined): value is PositionArchitectureSidebarId =>
    Object.values(sidebar.positionArchitecture).some((v) => v === value);

export const isSubscriptionBillingSidebarId = (value: SidebarId | undefined): value is SubscriptionBillingSidebarId =>
    Object.values(sidebar.subscriptionBilling).some((v) => v === value);
